import styled, { css, keyframes } from 'styled-components';

const flow = keyframes`
  from { background-position: 0% 50%; }
  to { background-position: 200% 50%; }
`;

const sweep = keyframes`
  from { transform: translateX(-130%); }
  to { transform: translateX(130%); }
`;

/**
 * The AI-brand gradient (blue → purple → magenta) with its first stop repeated
 * at the end so an animated `background-position` loops seamlessly. Pulled from
 * the theme (`theme.aiGradient`). We animate `background-position` (not a
 * `@property` angle) so it works across the full browserslist.
 */
export const flowingGradient = css`
  background: ${({ theme }) => theme.aiGradient};
  background-size: 200% 100%;
  animation: ${flow} 3s linear infinite;
`;

/**
 * Circular button around the per-item "refine with AI" icon — matches the
 * header action buttons (`HeaderIconWrapper`): a 40px white circle with a
 * subtle border + shadow.
 */
export const RefineIconButton = styled.div<{ $disabled?: boolean }>`
  flex: none;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e3e3e3;
  box-shadow: ${({ theme }) => theme.shadows.shadow2};
  border-radius: 50%;
  cursor: pointer;

  svg {
    display: block;
  }

  /* While the AI is (re-)running, refine stays visible but inert. */
  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

// A single left-to-right skewed glare that sweeps across a menu item when it's
// focused from the Changes list. The band is 50% wide, so translating its own
// width from -160% to 260% carries it fully off-screen on both sides.
const glareSweep = keyframes`
  from { transform: translateX(-160%) skewX(-20deg); }
  to { transform: translateX(260%) skewX(-20deg); }
`;

/**
 * Overlay laid over a menu item to play one glare sweep. Mounted only while the
 * item is highlighted (the parent clears the highlight after ~1s), so the
 * animation runs exactly once per focus. `border-radius: inherit` + `overflow:
 * hidden` keep the sweep clipped to the item's rounded corners.
 */
export const ItemGlare = styled.span`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 50%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.primary}66,
      transparent
    );
    transform: translateX(-160%) skewX(-20deg);
    animation: ${glareSweep} 0.9s ease-out forwards;
  }
`;

/**
 * Blows the 21×18 `AIStarsIcon` up to an illustration, for loading states that
 * want the AI mark above the copy. The icon hardcodes its own width/height, so
 * the size has to be forced from here.
 */
export const AIStarsIllustration = styled.div<{ $size?: string }>`
  display: flex;
  justify-content: center;

  svg {
    width: ${({ $size }) => $size ?? '96px'};
    height: auto;
  }
`;

/**
 * Wraps the menu (left) card. When `$active` (AI running) it renders a soft,
 * blurred, flowing gradient halo BEHIND the card. The halo lives on the wrapper
 * — not the card — because the card's `overflow: hidden` would clip it. Sized
 * with `flex: 7 1 0` so it takes ~70% of the split row (the 32px gap is absorbed
 * by flexbox).
 */
export const GlowWrapper = styled.div<{ $active?: boolean }>`
  position: relative;
  display: flex;
  flex: 7 1 0;
  min-width: 0;
  border-radius: 30px;

  /* Stacked on mobile: take natural (content) height, full width — not a
     flex height-ratio, which would squash the sibling AI card. */
  @media (max-width: 800px) {
    flex: none;
    width: 100%;
  }

  ${({ $active }) =>
    $active &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: -8px;
        border-radius: 38px;
        ${flowingGradient};
        filter: blur(18px);
        opacity: 0.55;
        z-index: -1;
      }

      @media (prefers-reduced-motion: reduce) {
        &::after {
          animation: none;
        }
      }
    `}
`;

/**
 * Crisp animated gradient border. Rendered INSIDE the card (so it sits above the
 * card's own `z-index`; the card's `overflow: hidden` clips it neatly to the
 * rounded edge). Uses the `mask-composite: exclude` recipe from
 * `button.styled.tsx` so only the 2px ring shows.
 */
export const GlowBorder = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 30px;
  padding: 2px;
  ${flowingGradient};
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 3;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Moving glare/shimmer sweep across the card (skeleton-style). Rendered inside
 * the card; its `overflow: hidden` plus the card's clip keeps the sweep within
 * the rounded rect.
 */
export const Shimmer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: 30px;
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      transparent 35%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 65%
    );
    transform: translateX(-130%);
    animation: ${sweep} 2.2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      display: none;
    }
  }
`;
